import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useHomeRepairs } from '../hooks/useHomeRepairs';
import { RepairItem } from '../types/repairs';
import { useChat } from './ChatContext';

// Define the shape of the context
interface HomeRepairsContextProps {
  repairs: RepairItem[];
  isLoading: boolean;
  error: string | null;
  fetchRepairs: () => Promise<void>;
  getRepairsSummary: () => {
    totalCount: number;
    unresolvedCount: number;
    highUrgencyCount: number;
    costRange: { min: number; max: number };
    unresolvedCostRange: { min: number; max: number };
    averageRepairCost: number;
  };
  getRepairsByUrgency: (urgency: 'high' | 'medium' | 'low') => RepairItem[];
  getRepairsByCategory: (category: string) => RepairItem[];
  getRepairsByArea: (areaName: string) => RepairItem[];
  toggleRepairResolution: (repairId: string) => Promise<void>;
  setError: (error: string | null) => void;
  homeId?: string;
  lastUpdated: number;
}

// Create the context
export const HomeRepairsContext = createContext<HomeRepairsContextProps | undefined>(undefined);

interface HomeRepairsProviderProps {
  children: ReactNode;
  homeId: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export function HomeRepairsProvider({ 
  children, 
  homeId,
  onSuccess,
  onError
}: HomeRepairsProviderProps) {
  // Use the hook with the homeId override
  const repairsData = useHomeRepairs({
    homeIdOverride: homeId,
    onSuccess,
    onError,
    showNotifications: true // Enable notifications for user-initiated actions
  });

  // Get chat context to listen for updates
  const chatContext = useChat();

  // Refresh repairs when chat is updated (new messages/images processed)
  useEffect(() => {
    if (chatContext.lastUpdated > 0) {
      // Add a small delay to ensure backend processing is complete
      const timer = setTimeout(() => {
        repairsData.fetchRepairs();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [chatContext.lastUpdated, repairsData.fetchRepairs]);
  
  return (
    <HomeRepairsContext.Provider value={{ 
      ...repairsData,
      homeId 
    }}>
      {children}
    </HomeRepairsContext.Provider>
  );
}

// Custom hook to use the context
export function useHomeRepairsContext() {
  const context = useContext(HomeRepairsContext);
  if (context === undefined) {
    throw new Error('useHomeRepairsContext must be used within a HomeRepairsProvider');
  }
  return context;
}